import { DocumentNode, gql } from '@apollo/client';
import {
  Box,
  HStack,
  IconButton,
  LinkBox,
  LinkOverlay,
  StackProps,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Url } from 'next/dist/shared/lib/router/router';
import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import {
  DownloadIcon,
  ImageIcon,
  PaperclipIcon,
} from '@/imports/ui/chakra/feather';

import { AttachmentDisplay_VersionFileFragment } from './graphql-types/AttachmentDisplay_VersionFileFragment';

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer').then((mod) => mod.PreviewRenderer),
  {
    ssr: false,
  }
);

type IAttachmentDisplayProps = {
  versionFiles: (AttachmentDisplay_VersionFileFragment | null)[] | null;
} & StackProps;

export const AttachmentDisplay: React.FC<IAttachmentDisplayProps> & {
  versionFileFragment: DocumentNode;
} = ({ versionFiles, ...stackProps }) => {
  if (!versionFiles || versionFiles.length === 0) {
    return null;
  }
  return (
    <VStack width="full" spacing="2" {...stackProps}>
      {versionFiles
        .filter(
          (
            versionFile
          ): versionFile is AttachmentDisplay_VersionFileFragment => {
            return !!versionFile;
          }
        )
        .map((versionFile) => {
          return (
            <IndividualAttachmentDisplay
              key={versionFile._id}
              versionFile={versionFile}
            />
          );
        })}
    </VStack>
  );
};

const IndividualAttachmentDisplay: React.FC<{
  versionFile: AttachmentDisplay_VersionFileFragment;
}> = ({ versionFile }) => {
  const router = useRouter();

  const href: Url = {
    query: {
      versionFileId: versionFile?._id.toString(),
      ...router.query,
    },
  };

  return (
    <>
      <PreviewRendererModal
        versionFile={versionFile}
        onClose={() => {
          const { versionFileId, ...query } = router.query;
          router.replace(
            { pathname: router.pathname, query: query },
            undefined,
            { shallow: true }
          );
        }}
        isOpen={router.query.versionFileId === versionFile._id}
      />
      <LinkBox width="full">
        <Box
          p="4"
          borderStyle="solid"
          borderColor="gray.200"
          borderWidth="1px"
          borderRadius="xl"
          width="full"
        >
          <HStack width="full">
            {versionFile.file?.isVideo || versionFile.file?.isImage ? (
              <ImageIcon />
            ) : (
              <PaperclipIcon />
            )}
            <LinkOverlay
              as={NextLink}
              // @ts-ignore - NextLink has different href prop and chakra doesn't seem to realize it
              href={href}
              shallow={false}
              scroll={false}
              flex="1"
            >
              <Text as="span" fontWeight="semibold">
                {versionFile.file?.name || 'no name '}
              </Text>
            </LinkOverlay>
            <NextLink
              href={versionFile.file?.url || ''}
              download={versionFile.file?.name}
            >
              <IconButton
                variant="ghost"
                colorScheme="gray"
                icon={<DownloadIcon color="gray.500" />}
                aria-label={`download ${versionFile.file?.name}`}
              />
            </NextLink>
          </HStack>
        </Box>
      </LinkBox>
    </>
  );
};

AttachmentDisplay.versionFileFragment = gql`
  fragment AttachmentDisplay_VersionFileFragment on VersionFile {
    _id
    file {
      _id
      name
      isVideo
      isImage
      url
    }
    ...PreviewRenderer_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
`;
